/**
 * Phaser department introduction (snippets/phaser-department-intro.liquid).
 *
 * - Desktop, header in menu mode: non-modal popover anchored under the visible
 *   department selector. No focus trap, no overlay. Escape, the close button,
 *   "Keep browsing" or a click outside closes it.
 * - Phones, and tablets in drawer mode: modal sheet (dialog.showModal()), so the
 *   background is inert, Escape closes it and focus returns afterwards.
 *
 * Auto-show: once per browser while the launch campaign is active
 * (<html data-pdn-campaign="active">, set before first paint by
 * phaser-department-state), on ordinary storefront pages, after the page has
 * settled, and only if nothing else is going on (open dialog/drawer, consent
 * banner, typing, nav or cart use). One attempt per page view, no retries.
 * The "seen" flag (localStorage "phaserDepartmentIntroSeen:<campaign id>") is
 * written when the panel is actually shown, or when the shopper uses the
 * selector themselves. No storage, no auto-show; manual help still works.
 *
 * Department choices are the shared [data-pdn-set] buttons, so switching goes
 * through custom-phaser-department-nav.js (one state, one storage key).
 *
 * Testing: add ?pdn_intro=show to any URL to open it on load, or clear the
 * localStorage key above.
 */

const SEEN_PREFIX = 'phaserDepartmentIntroSeen:';
const AUTO_DELAY_MS = 2500;
const EDGE = 16;
const GAP = 12;
const root = document.documentElement;

/** @returns {Storage | null} localStorage if it can actually be written. */
function writableStorage() {
  try {
    const store = window.localStorage;
    store.setItem('__pdn_intro_test', '1');
    store.removeItem('__pdn_intro_test');
    return store;
  } catch {
    return null;
  }
}

/** @param {Element | null} el */
function isVisible(el) {
  return el instanceof HTMLElement && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
}

class PhaserDepartmentIntro extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  dialog = null;
  /** @type {AbortController | null} */
  abort = null;
  /** @type {HTMLElement | null} */
  trigger = null;
  closedHandled = true;
  manual = false;
  seenKey = '';
  timer = 0;
  interacted = false;
  frame = 0;

  connectedCallback() {
    const dialog = this.querySelector('dialog.pdn-intro');
    if (!(dialog instanceof HTMLDialogElement)) return;
    this.dialog = dialog;

    // A Theme Editor section reload leaves the previous instance's dialog in
    // <body>; remove it so there is only ever one panel.
    for (const old of document.querySelectorAll('body > dialog.pdn-intro')) {
      if (old === dialog) continue;
      if (old instanceof HTMLDialogElement && old.open) old.close();
      old.remove();
    }
    // Out of the header so fixed positioning isn't affected by header transforms.
    document.body.append(dialog);

    this.seenKey = SEEN_PREFIX + (this.dataset.campaignId || 'v1');
    this.abort = new AbortController();
    const { signal } = this.abort;

    document.addEventListener('click', this.onClick, { signal });
    document.addEventListener('keydown', this.onKeydown, { signal });
    document.addEventListener('pointerdown', this.onPointerDown, { signal, capture: true });
    document.addEventListener('phaser:department-change', this.onDepartmentChange, { signal });
    dialog.addEventListener('close', this.onClose, { signal });
    dialog.addEventListener('cancel', this.onCancel, { signal });
    window.addEventListener('resize', this.queueReposition, { signal, passive: true });
    window.addEventListener('scroll', this.queueReposition, { signal, passive: true });

    for (const type of ['pointerdown', 'keydown', 'focusin', 'input']) {
      document.addEventListener(type, this.noteInteraction, { signal, capture: true, passive: true });
    }

    for (const opener of document.querySelectorAll('[data-pdn-intro-open]')) {
      opener.setAttribute('aria-haspopup', 'dialog');
      opener.setAttribute('aria-expanded', 'false');
    }

    this.scheduleAuto();
  }

  disconnectedCallback() {
    this.abort?.abort();
    clearTimeout(this.timer);
    cancelAnimationFrame(this.frame);
    if (this.dialog) {
      if (this.dialog.open) this.dialog.close();
      this.dialog.remove();
    }
    root.classList.remove('pdn-intro-lock');
  }

  // ── Opening and closing ──────────────────────────────────────────────────

  /** The selector copy the popover points at, if one is showing. */
  get anchor() {
    const bars = document.querySelectorAll('.pdn-bar--header, .pdn-bar--strip');
    return [...bars].find(isVisible) || null;
  }

  get useModal() {
    const header = document.getElementById('header-component');
    return window.innerWidth < 750 || header?.dataset.menuStyle === 'drawer' || !this.anchor;
  }

  /**
   * @param {{ manual: boolean, trigger?: HTMLElement | null, record?: boolean }} options
   */
  open({ manual, trigger = null, record = true }) {
    const dialog = this.dialog;
    if (!dialog || dialog.open) return;

    this.trigger = trigger;
    this.manual = manual;
    this.closedHandled = false;
    clearTimeout(this.timer);

    const previous = document.activeElement;
    if (this.useModal) {
      dialog.showModal();
      root.classList.add('pdn-intro-lock');
    } else {
      dialog.show();
      this.position();
      if (!manual) {
        // Don't take focus from the shopper when it opens by itself.
        if (previous instanceof HTMLElement && previous !== document.body) previous.focus({ preventScroll: true });
        else if (document.activeElement instanceof HTMLElement && dialog.contains(document.activeElement)) document.activeElement.blur();
      }
    }
    if (manual) dialog.querySelector('#PdnIntroTitle')?.focus({ preventScroll: true });

    this.setExpanded(true);
    if (record) this.markSeen();
  }

  close() {
    if (!this.dialog?.open) return;
    this.dialog.close();
    // Clean up here rather than only on the async `close` event, which isn't
    // guaranteed to fire promptly (e.g. in background tabs).
    this.onClose();
  }

  /** Modal Escape: route through close() so cleanup always runs. */
  onCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  onClose = () => {
    if (this.closedHandled) return;
    this.closedHandled = true;
    root.classList.remove('pdn-intro-lock');
    this.setExpanded(false);
    const trigger = this.trigger;
    this.trigger = null;
    // Return focus to where the shopper opened it from, if it's still there.
    if (this.manual && trigger && trigger.isConnected && isVisible(trigger)) {
      trigger.focus({ preventScroll: true });
    }
  };

  /** @param {boolean} value */
  setExpanded(value) {
    for (const opener of document.querySelectorAll('[data-pdn-intro-open]')) {
      opener.setAttribute('aria-expanded', String(value));
    }
  }

  markSeen() {
    try {
      window.localStorage.setItem(this.seenKey, new Date().toISOString());
    } catch {
      // No storage: auto-show is already off in that case.
    }
  }

  // ── Popover position ─────────────────────────────────────────────────────

  queueReposition = () => {
    if (!this.dialog?.open) return;
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.position());
  };

  position() {
    const dialog = this.dialog;
    if (!dialog?.open || dialog.matches(':modal')) return;

    const anchor = this.anchor;
    if (!anchor || this.useModal) {
      this.close();
      return;
    }

    const rect = anchor.getBoundingClientRect();
    if (rect.bottom <= 0) {
      this.close();
      return;
    }

    const viewport = document.documentElement.clientWidth;
    const width = dialog.offsetWidth;
    const centred = anchor.classList.contains('pdn-bar--strip');
    let left = centred ? rect.left + rect.width / 2 - width / 2 : rect.left;
    left = Math.max(EDGE, Math.min(left, viewport - width - EDGE));

    const control = anchor.querySelector('.pdn') || anchor;
    const controlRect = control.getBoundingClientRect();
    const arrow = Math.max(24, Math.min(controlRect.left + controlRect.width / 2 - left, width - 24));
    const top = rect.bottom + GAP;

    dialog.style.setProperty('--pdn-intro-left', `${Math.round(left)}px`);
    dialog.style.setProperty('--pdn-intro-top', `${Math.round(top)}px`);
    dialog.style.setProperty('--pdn-intro-arrow', `${Math.round(arrow)}px`);
    dialog.style.setProperty('--pdn-intro-max-height', `${Math.max(240, Math.round(window.innerHeight - top - EDGE))}px`);
  }

  // ── Events ───────────────────────────────────────────────────────────────

  /** @param {MouseEvent} event */
  onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // Modal sheet: a tap on the backdrop lands on the <dialog> itself. Only
    // count it as a backdrop tap when the point is outside the sheet's box.
    if (target === this.dialog && this.dialog.open && this.dialog.matches(':modal')) {
      const rect = this.dialog.getBoundingClientRect();
      const { clientX: x, clientY: y } = event;
      const isPointer = x !== 0 || y !== 0;
      if (isPointer && (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom)) {
        this.close();
        return;
      }
    }

    const closer = target.closest('[data-pdn-intro-close]');
    if (closer && this.dialog?.contains(closer)) {
      this.close();
      return;
    }

    const opener = target.closest('[data-pdn-intro-open]');
    if (!(opener instanceof HTMLElement)) return;

    if (this.dialog?.open) {
      this.close();
      return;
    }

    // From the mobile drawer: close the drawer first (it traps focus), then
    // open the sheet and hand focus back to the menu button afterwards.
    const drawer = opener.closest('header-drawer');
    if (drawer && 'close' in drawer && typeof drawer.close === 'function') {
      drawer.close();
      const summary = drawer.querySelector('summary');
      setTimeout(() => this.open({ manual: true, trigger: summary instanceof HTMLElement ? summary : null }), 350);
      return;
    }

    this.open({ manual: true, trigger: opener });
  };

  /** @param {KeyboardEvent} event */
  onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.dialog?.open || this.dialog.matches(':modal')) return;
    this.close();
  };

  /** Close the popover on an outside press without blocking where it lands. */
  onPointerDown = (event) => {
    const dialog = this.dialog;
    if (!dialog?.open || dialog.matches(':modal')) return;
    const target = event.target instanceof Node ? event.target : null;
    if (!target || dialog.contains(target)) return;
    if (target instanceof Element && target.closest('[data-pdn-intro-open]')) return;
    this.close();
  };

  /** @param {Event} event */
  onDepartmentChange = (event) => {
    const origin = event instanceof CustomEvent ? event.detail?.origin : null;
    if (origin === 'intro') {
      this.close();
    } else if (origin === 'selector') {
      // Using the selector counts as having found the feature.
      clearTimeout(this.timer);
      if (root.dataset.pdnCampaign === 'active' && writableStorage()) this.markSeen();
    }
  };

  /** Remember that the shopper has started doing something on this page. */
  noteInteraction = (event) => {
    if (this.interacted) return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target || this.dialog?.contains(target)) return;
    if (
      event.type === 'input' ||
      target.closest(
        '#header-component, input, textarea, select, [contenteditable], dialog, cart-drawer-component, .cart-drawer, [data-cart], .menu-drawer, [data-pdn-intro-open]',
      )
    ) {
      this.interacted = true;
    }
  };

  // ── Automatic launch introduction ────────────────────────────────────────

  scheduleAuto() {
    const params = new URLSearchParams(window.location.search);
    const forced = params.get('pdn_intro') === 'show';

    if (this.dataset.editor === 'true') {
      if (this.dataset.editorPreview === 'true') this.whenSettled(() => this.open({ manual: false, record: false }), 600);
      return;
    }

    if (forced) {
      this.whenSettled(() => this.open({ manual: false }), 800);
      return;
    }

    if (root.dataset.pdnCampaign !== 'active' || this.dataset.autoPage !== 'true') return;
    const store = writableStorage();
    if (!store || store.getItem(this.seenKey)) return;

    this.whenSettled(() => {
      if (this.interacted || this.blocked()) return;
      this.open({ manual: false });
    }, AUTO_DELAY_MS);
  }

  /**
   * @param {() => void} run
   * @param {number} delay
   */
  whenSettled(run, delay) {
    const go = () => {
      this.timer = window.setTimeout(run, delay);
    };
    // Start from DOM ready, not window load: the homepage's images and apps can
    // hold `load` back long enough that the shopper is already busy.
    if (document.readyState !== 'loading') go();
    else document.addEventListener('DOMContentLoaded', go, { once: true, signal: this.abort?.signal });
  }

  /** Something else needs the shopper's attention: don't interrupt. */
  blocked() {
    if (document.querySelector('dialog[open]:not(.pdn-intro)')) return true;
    if (document.querySelector('details.menu-drawer-container[open]')) return true;
    for (const el of document.querySelectorAll('[aria-modal="true"], [role="dialog"], #shopify-pc__banner, .shopify-pc__banner__dialog')) {
      if (el !== this.dialog && isVisible(el)) return true;
    }
    if (document.querySelector('.cdm-nav__item:hover, .cdm-nav__item:focus-within')) return true;
    const active = document.activeElement;
    if (active instanceof Element && active.matches('input, textarea, select, [contenteditable]')) return true;
    return false;
  }
}

if (!customElements.get('phaser-department-intro')) {
  customElements.define('phaser-department-intro', PhaserDepartmentIntro);
}
